import { initializeApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore, connectFirestoreEmulator } from 'firebase/firestore';
import { handleIndexedDBError } from '../utils/indexedDbErrorHandler';

// Ensure all required config values are present
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID'
] as const;

// Check for missing environment variables
const missingEnvVars = requiredEnvVars.filter(
  (envVar) => !import.meta.env[envVar]
);

if (missingEnvVars.length > 0) {
  throw new Error(
    `Missing required environment variables: ${missingEnvVars.join(', ')}`
  );
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Initialize Firebase with error handling
const app = initializeApp(firebaseConfig);

// Get Auth instance with error handling
let auth: Auth;
try {
  auth = getAuth(app);
} catch (error) {
  console.error('Failed to initialize Firebase Auth:', error);
  throw error;
}

// Initialize Firestore with enhanced error handling
let db: Firestore;
try {
  db = getFirestore(app);
  
  // Add global error handler for Firestore operations
  if (typeof window !== 'undefined') {
    // Listen for Firestore errors and handle IndexedDB issues
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason;
      
      // Check if it's a Firestore/IndexedDB related error
      if (error && typeof error === 'object') {
        const errorMessage = error.message || '';
        const errorCode = error.code || '';
        
        // Detect Firestore offline/IndexedDB errors
        if (errorMessage.includes('IndexedDB') || 
            errorMessage.includes('Connection to Indexed Database server lost') ||
            errorCode === 'failed-precondition' ||
            errorCode === 'unavailable') {
          
          console.error('Firestore IndexedDB error detected:', error);
          handleIndexedDBError(error, 'firestore');
          
          // Prevent the unhandled rejection from bubbling up
          event.preventDefault();
        }
      }
    });

    // Add a global handler for Firestore network errors
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      // Check if this is a Firestore network error
      const message = args.join(' ');
      if (message.includes('Firestore') && 
          (message.includes('network') || message.includes('offline') || message.includes('unavailable'))) {
        
        // Handle as a potential IndexedDB/connection issue
        const syntheticError = new Error(message);
        handleIndexedDBError(syntheticError, 'firestore-network');
      }
      
      // Call original console.error
      originalConsoleError.apply(console, args);
    };
  }
  
} catch (error) {
  console.error('Failed to initialize Firestore:', error);
  
  // Check if this is an IndexedDB related error
  if (error && typeof error === 'object') {
    const errorMessage = (error as Error).message || '';
    if (errorMessage.includes('IndexedDB') || errorMessage.includes('database')) {
      handleIndexedDBError(error, 'firestore-init');
    }
  }
  
  throw error;
}

// Enhanced connection state monitoring
if (typeof window !== 'undefined') {
  // Monitor online/offline status
  window.addEventListener('online', () => {
    console.log('Connection restored, Firestore should reconnect automatically');
  });

  window.addEventListener('offline', () => {
    console.log('Connection lost, Firestore will attempt to reconnect when online');
  });

  // Monitor visibility changes (Safari specific optimization)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Page became visible again, good time to check connection health
      console.log('Page visible again, checking connection health');
    }
  });
}

// Export both instances
export { auth, db };

// Export helper function to check Firestore connection
export const checkFirestoreConnection = async (): Promise<boolean> => {
  try {
    // Simple test to check if Firestore is accessible
    // We just check if the db instance exists and is properly initialized
    return db !== null && typeof db === 'object';
  } catch (error) {
    console.error('Firestore connection check failed:', error);
    if (error && typeof error === 'object') {
      handleIndexedDBError(error, 'connection-check');
    }
    return false;
  }
};